import { supabase } from '../lib/supabase';
import { Database } from '../types/supabase';

type Tables = Database['public']['Tables'];
type Enums = Database['public']['Enums'];

export class ApiService {
  // User Dashboard
  async getUserRequirements(userId?: string) {
    const { data: { user } } = await supabase.auth.getUser();
    const targetUserId = userId || user?.id;
    
    if (!targetUserId) throw new Error('User not authenticated');
    
    const { data, error } = await supabase.rpc('get_user_requirements', {
      p_user_id: targetUserId
    });
    
    if (error) throw error;
    return data;
  }

  async getDashboardStats(organizationId: string) {
    const { data, error } = await supabase.rpc('get_dashboard_stats', {
      p_org_id: organizationId
    });
    
    if (error) throw error;
    return data;
  }

  // Document Management
  async uploadDocument(requirementId: string, file: File, documentData: {
    issue_date?: string;
    expiry_date?: string;
  }) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Upload file to storage
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `${user.id}/${fileName}`;

    const { data: uploadData, error: uploadError } = await supabase.storage
      .from('documents')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (uploadError) throw uploadError;

    // Create document record
    const { data: document, error: dbError } = await supabase
      .from('documents')
      .insert({
        requirement_id: requirementId,
        file_location: filePath,
        original_filename: file.name,
        mime_type: file.type,
        file_size_bytes: file.size,
        status: 'pending_approval' as Enums['document_status'],
        issue_date: documentData.issue_date,
        expiry_date: documentData.expiry_date,
      })
      .select()
      .single();

    if (dbError) throw dbError;

    // Update requirement with latest document
    await supabase
      .from('user_document_requirements')
      .update({ latest_document_id: document.id })
      .eq('id', requirementId);

    return document;
  }

  async getDocumentUrl(filePath: string) {
    const { data } = supabase.storage
      .from('documents')
      .getPublicUrl(filePath);
    
    return data.publicUrl;
  }

  async downloadDocument(filePath: string) {
    const { data, error } = await supabase.storage
      .from('documents')
      .download(filePath);
    
    if (error) throw error;
    return data;
  }

  // Admin Functions
  async getAllUsers(organizationId?: string) {
    let query = supabase
      .from('users')
      .select(`
        *,
        user_organization!inner(organization_id)
      `)
      .eq('is_active', true)
      .eq('is_deleted', false);

    if (organizationId) {
      query = query.eq('user_organization.organization_id', organizationId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  }

  async getUsersWithRequirements(organizationId?: string) {
    let query = supabase
      .from('users')
      .select(`
        *,
        user_organization!inner(organization_id),
        user_document_requirements(
          id,
          status,
          due_date,
          is_current,
          document_definitions(name),
          documents(status, expiry_date)
        )
      `)
      .eq('is_active', true)
      .eq('is_deleted', false);

    if (organizationId) {
      query = query.eq('user_organization.organization_id', organizationId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  }

  async createUser(userData: {
    email: string;
    password: string;
    full_name: string;
    role: Enums['user_role'];
    organization_id: string;
  }) {
    // Create auth user
    const { data: authData, error: authError } = await supabase.auth.admin.createUser({
      email: userData.email,
      password: userData.password,
      email_confirm: true,
      user_metadata: {
        full_name: userData.full_name,
        role: userData.role
      }
    });

    if (authError) throw authError;

    // Create user organization relationship
    const { error: orgError } = await supabase
      .from('user_organization')
      .insert({
        user_id: authData.user.id,
        organization_id: userData.organization_id
      });

    if (orgError) throw orgError;

    // Create initial requirements
    await supabase.rpc('create_initial_requirements', {
      p_user_id: authData.user.id,
      p_org_id: userData.organization_id
    });

    return authData.user;
  }

  // Document Review
  async getPendingDocuments(organizationId?: string) {
    let query = supabase
      .from('documents')
      .select(`
        *,
        user_document_requirements!inner(
          user_id,
          definition_id,
          users!inner(
            email,
            full_name,
            user_organization!inner(organization_id)
          ),
          document_definitions(name)
        )
      `)
      .eq('status', 'pending_approval')
      .eq('is_deleted', false);

    if (organizationId) {
      query = query.eq('user_document_requirements.users.user_organization.organization_id', organizationId);
    }

    const { data, error } = await query;
    if (error) throw error;
    return data;
  }

  async approveDocument(documentId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase.rpc('approve_document', {
      p_document_id: documentId,
      p_approver_id: user.id
    });

    if (error) throw error;
  }

  async rejectDocument(documentId: string, reason: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { error } = await supabase.rpc('reject_document', {
      p_document_id: documentId,
      p_reviewer_id: user.id,
      p_reason: reason
    });

    if (error) throw error;
  }

  // Categories
  async getCategories(organizationId: string) {
    const { data, error } = await supabase
      .from('categories')
      .select('*')
      .eq('organization_id', organizationId)
      .order('name');

    if (error) throw error;
    return data;
  }

  async createCategory(category: Tables['categories']['Insert']) {
    const { data, error } = await supabase
      .from('categories')
      .insert(category)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async updateCategory(id: string, updates: Tables['categories']['Update']) {
    const { data, error } = await supabase
      .from('categories')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Document Definitions
  async getDocumentDefinitions(organizationId: string) {
    const { data, error } = await supabase
      .from('document_definitions')
      .select(`
        *,
        categories(id, name)
      `)
      .eq('organization_id', organizationId)
      .eq('is_active', true)
      .order('name');

    if (error) throw error;
    return data;
  }

  async createDocumentDefinition(definition: Tables['document_definitions']['Insert']) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('document_definitions')
      .insert({
        ...definition,
        created_by_user_id: user.id
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  async updateDocumentDefinition(id: string, updates: Tables['document_definitions']['Update']) {
    const { data, error } = await supabase
      .from('document_definitions')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Organizations
  async getOrganization(id: string) {
    const { data, error } = await supabase
      .from('organizations')
      .select('*')
      .eq('id', id)
      .single();

    if (error) throw error;
    return data;
  }

  async updateOrganization(id: string, updates: Tables['organizations']['Update']) {
    const { data, error } = await supabase
      .from('organizations')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Notification Settings
  async getNotificationSettings(organizationId: string) {
    const { data, error } = await supabase
      .from('notification_settings')
      .select('*')
      .eq('organization_id', organizationId);

    if (error) throw error;
    return data;
  }

  async updateNotificationSettings(id: string, updates: Tables['notification_settings']['Update']) {
    const { data, error } = await supabase
      .from('notification_settings')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  }

  // Admin Report Functions
  async getAdminReportData(year?: number) {
    const { data, error } = await supabase.rpc('get_admin_report_data', {
      target_year: year || new Date().getFullYear()
    });
    
    if (error) throw error;
    return data || [];
  }

  async recalculateUserProgress(userId?: string, year?: number) {
    const { data, error } = await supabase.rpc('calculate_user_monthly_progress', {
      target_user_id: userId,
      target_year: year
    });
    
    if (error) throw error;
    return data;
  }

  async refreshDocumentPeriodMappings() {
    const { data, error } = await supabase.rpc('populate_document_period_mappings');
    
    if (error) throw error;
    return data;
  }

  // Real-time subscriptions
  subscribeToDocumentUpdates(userId: string, callback: (payload: any) => void) {
    return supabase
      .channel('document-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'documents',
          filter: `user_id=eq.${userId}`
        },
        callback
      )
      .subscribe();
  }

  subscribeToRequirementUpdates(userId: string, callback: (payload: any) => void) {
    return supabase
      .channel('requirement-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_document_requirements',
          filter: `user_id=eq.${userId}`
        },
        callback
      )
      .subscribe();
  }

  subscribeToProgressUpdates(callback: (payload: any) => void) {
    return supabase
      .channel('progress-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_monthly_progress'
        },
        callback
      )
      .subscribe();
  }

  // Dashboard Enhanced Functions
  async getRecentActivity(limit: number = 20, timePeriod: string = '7d') {
    const { data, error } = await supabase.rpc('get_recent_activity', {
      activity_limit: limit,
      time_period: timePeriod
    });

    if (error) throw error;
    return data || [];
  }

  async getOrganizationalYearlyProgress(year?: number, organizationNames?: string[]) {
    const { data, error } = await supabase.rpc('get_organizational_yearly_progress', {
      target_year: year,
      organization_names: organizationNames
    });

    if (error) throw error;
    return data || [];
  }

  async getDashboardTimeMetrics(timePeriod: string = 'today', organizationFilter?: string) {
    const { data, error } = await supabase.rpc('get_dashboard_time_metrics', {
      time_period: timePeriod,
      organization_filter: organizationFilter
    });

    if (error) throw error;
    return data?.[0] || null;
  }

  // User Internal Status Management
  async updateUserInternalStatus(userId: string, isInternal: boolean) {
    const { data, error } = await supabase
      .from('users')
      .update({ is_internal: isInternal })
      .eq('id', userId)
      .select()
      .single();

    if (error) throw error;
    return data;
  }
}

export const api = new ApiService();